'use client';

import { useEffect, useRef } from 'react';

import { useShellRuntime } from '@/components/shell/shell-runtime-provider';
import { t } from '@/lib/design/tokens';

interface UserAvatarMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserAvatarMenu({ open, onOpenChange }: UserAvatarMenuProps) {
  const { creditsLabel, isLoading, orgInitial, orgName, userEmail } =
    useShellRuntime();
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const onDocClick = (event: MouseEvent) => {
      if (!panelRef.current) return;
      if (panelRef.current.contains(event.target as Node)) return;
      onOpenChange(false);
    };
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false);
    };

    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onDocClick);
    }, 0);
    document.addEventListener('keydown', onEsc);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open, onOpenChange]);

  return (
    <>
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-label="Account menu"
        aria-expanded={open}
        style={{
          width: 28,
          height: 28,
          borderRadius: 999,
          background: t.espressoMuted,
          border: `1px solid ${t.borderStrong}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          fontWeight: 600,
          color: t.espresso,
          fontFamily: t.font,
          cursor: 'pointer',
          padding: 0,
        }}
      >
        {orgInitial}
      </button>

      {open && (
        <div
          ref={panelRef}
          role="menu"
          style={{
            position: 'fixed',
            top: 56,
            right: 16,
            width: 280,
            background: t.card,
            border: `1px solid ${t.cardBorder}`,
            borderRadius: 12,
            padding: '16px 0',
            zIndex: 100,
            fontFamily: t.font,
            boxShadow: t.shadowLg,
          }}
        >
          <div style={{ padding: '0 16px 12px' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: t.text1 }}>
              {orgName ?? 'Workspace'}
            </div>
            <div style={{ fontSize: 11, color: t.textM, marginTop: 4 }}>
              {userEmail ??
                (isLoading ? 'Loading workspace…' : 'No email available')}
            </div>
          </div>

          <div style={{ height: 1, background: t.border, margin: '0 16px' }} />

          <div style={{ padding: '12px 16px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ fontSize: 13, color: t.text1 }}>
                Credits:{' '}
                <span
                  style={{
                    color: t.espresso,
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {creditsLabel ?? (isLoading ? 'Loading…' : 'Unavailable')}
                </span>
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
